import React from 'react';
import { GetStaticPropsContext } from 'next';
import getAdditionalPageContent from '../utils/getAdditionalPageContent';
import ImageHeader from '../components/common/ImageHeader';
import PageTitle from '../components/common/PageTitle';
import { getClient } from '../contentful/contentfulClient';
import renderDocument from '../contentful/renderDocument';
import PreviewMode from '../components/PreviewMode';

export async function getStaticPaths() {
  const pages = [
    'land',
    'about',
    'joint-ventures',
    'contracting',
    'privacy',
    'legal',
    'cookie-policy',
    'consumer-code',
    'design',
    'contact',
    'careers',
  ];
  return {
    paths: pages.map((page) => ({ params: { slug: page } })),
    fallback: 'blocking',
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const MINUTES = 60;
  const revalidate = 60 * MINUTES;
  const slug = context.params.slug;

  const { getComponentProps } = getAdditionalPageContent(slug as string);
  let componentProps: any;

  if (getComponentProps) {
    componentProps = await getComponentProps(context);
  }

  const client = getClient(context.preview);

  const entries = await client.getEntries({
    content_type: 'page',
    'fields.slug': slug,
  });

  const entry = entries.items && entries.items?.[0];

  if (!entry) {
    return {
      notFound: true,
      revalidate,
    };
  }

  const fields = entry.fields as any;

  // Clear empty paragraph at bottom
  const item = (fields as any)?.content.content[fields.content.content.length - 1];
  if (item?.nodeType === 'paragraph' && item.content[0].value === '') {
    fields.content.content.pop();
  }

  return {
    props: {
      entry,
      slug,
      ...(componentProps && { componentProps }),
      ...(context.preview && { preview: context.preview }),
    },
    revalidate, // In seconds
  };
}

const ContentfulPage = ({ entry, slug, componentProps, preview }) => {
  const content = renderDocument(entry.fields.content);
  const extra = getAdditionalPageContent(slug);
  const title = entry.fields.title;
  const dark = entry.fields.dark;
  const rightImage = entry.fields.rightImage?.fields.file.url;
  const headerImage = entry.fields.headerImage?.fields.file.url;
  const HeaderImageContainer = ({ children }) =>
    headerImage ? (
      <>
        <ImageHeader url={headerImage} title={entry.fields.title} />

        <section className="container space">
          <div className="clearfix">{children}</div>
        </section>
      </>
    ) : slug === 'about' ? (
      children
    ) : (
      <section className="container">{children}</section>
    );

  return (
    <section className={slug} style={dark ? { backgroundColor: '#2E3343', color: 'white' } : {}}>
      <PageTitle title={title} />
      {preview && <PreviewMode />}

      <HeaderImageContainer>
        {rightImage ? (
          <>
            <div className="col-md-8">{content}</div>
            <div className="col-md-4">
              <div className="img-responsive">
                <img src={rightImage} alt="" className="thumbnail img-responsive" />
              </div>
            </div>
          </>
        ) : (
          content
        )}
      </HeaderImageContainer>
      <extra.Footer {...componentProps} />
    </section>
  );
};

export default ContentfulPage;
